import { useParams } from 'react-router-dom';

import { LocalType, Model, Nav, Result, Toggle } from '@gameonly/core';

import { TitleBlock } from '../ui/Block';
import { PageContainer } from '../ui/Container';
import { ReactComponent as ExportIcon } from '../ui/icons/export.svg';
import { Abacus } from './ScoreCardComponent';

import { useProject } from '../project/context/useProject';

import styled from '@emotion/styled';
import Bar1DComponent, { Bar1DLegend } from './Bar1DComponent';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authenticatedFetch } from '../authentication/authenticatedFetch';
import useUser, { isUserSubscribed } from '../authentication/context/useUser';
import SubscriptionModal from '../subscription/SubscriptionModal';
import SubscriptionTeasing from '../subscription/SubscriptionTeasing';
import CustomLoadingButton from '../ui/LoadingButton';
import BarSingle1DComponent, {
  BarSingle1DLegend,
} from './BarSingle1DComponent';
import BarStacked1DComponent from './BarStacked1DComponent';
import BarStacked2DComponent from './BarStacked2DComponent';
import GlobalScoreComponent from './GlobalScore';
import NavComponent from './Nav';
import PdfDownloadDialog, { PdfDownloadContext } from './PdfDownloadDialog';
import Pie1DComponent, { Pie1DLegend } from './Pie1DComponent';
import ResultGraph from './ResultGraph';
import SectionTitle, { SectionSubtitle } from './SectionTitle';
import Table from './Table';
import TextBlock from './TextBlock';
import ToggleComponent from './ToggleComponent';
import TreemapComponent from './TreemapComponent';

const ResultsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  border-radius: 8px;
  margin-bottom: 16px;
`;

export const StickyWrapper = styled.div<{
  top?: number;
  paddingTop?: number;
  backgroundColor?: string;
}>`
  position: sticky;
  top: ${({ top = 0 }) => `${top}px`};
  padding-top: ${({ paddingTop = 0 }) => `${paddingTop}px`};
  background-color: ${({ backgroundColor = 'white' }) => backgroundColor};
  z-index: 10;
`;

export const StickyHeader = styled(HeaderContainer)`
  position: sticky;
  top: 88px;
  background-color: white;
  z-index: 10;
`;

const StickyDemoBlock = styled(TitleBlock)`
  text-align: center;
  color: white;

  h3 {
    font-size: 2rem;
    font-weight: bold;
  }

  p {
    font-size: 1rem;
  }
`;

const CategoryContainer = styled.div`
  padding: 16px;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  background-color: white;
  border-radius: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: center;
`;
const getResultsByCategories = (results: Result[]) => {
  let resultsByCategories: Result[][] = [];
  let categoryResults: Result[] = [];
  results.forEach((result) => {
    if (result.type === 'title' && categoryResults.length > 0) {
      resultsByCategories.push(categoryResults);
      categoryResults = [result];
    } else {
      categoryResults.push(result);
    }
  });
  resultsByCategories.push(categoryResults);
  return resultsByCategories;
};

const getResultsFiltered = (result: Result, filters: string[] | undefined) => {
  return (
    !filters ||
    !('filter' in result) ||
    result.filter?.length === 0 ||
    filters.includes(result.filter as string)
  );
};

export const getResultsToDisplay = (
  results: Result[],
  filters: string[] | undefined,
) => {
  return results
    .filter(
      (result) =>
        result.scope === 'complete' &&
        result.type !== 'nav' &&
        result.type !== 'toggle' &&
        (!('display' in result) || result.display),
    )
    .filter((result) => getResultsFiltered(result, filters));
};

export const getNav = (results: Result[], filters: string[] | undefined) => {
  return results
    .filter((result) => getResultsFiltered(result, filters))
    .find((result) => result.type === 'nav') as Nav;
};

const Results = () => {
  const { projectId } = useParams();
  const { project } = useProject(projectId);
  const { user } = useUser();
  const { t, i18n } = useTranslation(['project']);
  const [toggle, setToggle] = useState<Toggle | undefined>(undefined);
  const [isToggleSet, setIsToggleSet] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  useEffect(() => {
    const toggleTemp = project?.results.find(
      (result) => result.type === 'toggle',
    ) as Toggle | undefined;
    if (toggleTemp) setToggle(toggleTemp);
    setIsToggleSet(true);
  }, [project]);

  useEffect(() => {
    if (user && !isUserSubscribed(user)) {
      setShowModal(true);
    }
  }, [user]);

  if (!project || !isToggleSet) {
    return null;
  }

  const pdfContext: PdfDownloadContext = {
    label: t('project:pdf.reportTypeChoice.title'),
    fetch: async (reportType) => {
      const query = `localType=${
        toggle?.selected as LocalType
      }&reportType=${reportType}&lang=${i18n.language}`;
      return authenticatedFetch(
        `${process.env.REACT_APP_API_URL}/projects/${projectId}/pdf?${query}`,
      );
    },
    fileName: (reportType) =>
      `${project?.name.toLowerCase().replace(/\s/g, '-')}-${reportType}-${
        i18n.language
      }.pdf`,
  };

  const filters = toggle ? [toggle.selected] : undefined;
  const resultsToDisplay = getResultsToDisplay(project.results, filters);
  const nav = getNav(project.results, filters);
  const resultsByCategories = getResultsByCategories(resultsToDisplay);

  const subscribed = isUserSubscribed(user);

  return (
    <PageContainer noGap noMarginTop>
      <PdfDownloadDialog
        open={pdfDialogOpen}
        onClose={() => setPdfDialogOpen(false)}
        context={pdfContext}
      />
      <SubscriptionModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={t('common:subscription.completeResults.modalTitle')}
        text={t('common:subscription.completeResults.modalText')}
        link={t('common:subscription.completeResults.knowMoreLink')}
      />

      {subscribed ? (
        <TitleBlock background="var(--results-bg)" style={{ margin: '16px 0' }}>
          {project.name && (
            <h3>
              {project.name} - {t('project:results-page.title')}
            </h3>
          )}
          {!project.name && <h3>{t('project:results-page.title')}</h3>}
          <p>{t('project:results-page.subtitle')}</p>
          <ButtonContainer>
            <CustomLoadingButton
              onClick={() => setPdfDialogOpen(true)}
              loading={false}
              startIcon={<ExportIcon />}
            >
              {t('common:subscription.pdfDownload.teasingButton')}
            </CustomLoadingButton>
          </ButtonContainer>
        </TitleBlock>
      ) : (
        <StickyWrapper
          top={64}
          paddingTop={16}
          backgroundColor="var(--backgroundColor)"
        >
          <StickyDemoBlock
            background="var(--fushia)"
            style={{ marginBottom: '16px' }}
          >
            <h3>{t('project:results-page.demoTitle')}</h3>
            <p>{t('project:results-page.demoSubtitle')}</p>
            <ButtonContainer>
              <SubscriptionTeasing
                teasingButton={t(
                  'common:subscription.pdfDownload.teasingButton',
                )}
                text={t('common:subscription.pdfDownload.modalText')}
                title={t('common:subscription.pdfDownload.modalTitle')}
                link={t('common:subscription.pdfDownload.knowMoreLink')}
              />
            </ButtonContainer>
          </StickyDemoBlock>
        </StickyWrapper>
      )}

      <ResultsContainer>
        {subscribed ? (
          <StickyWrapper
            top={64}
            paddingTop={16}
            backgroundColor="var(--backgroundColor)"
          >
            <StickyHeader>
              {nav && <NavComponent result={nav} model={project?.model} />}

              {toggle && (
                <ToggleComponent
                  toggle={toggle}
                  setToggle={setToggle}
                  modelType={project.model.type}
                />
              )}
            </StickyHeader>
          </StickyWrapper>
        ) : (
          <HeaderContainer>
            {nav && <NavComponent result={nav} model={project?.model} />}

            {toggle && (
              <ToggleComponent
                toggle={toggle}
                setToggle={setToggle}
                modelType={project.model.type}
              />
            )}
          </HeaderContainer>
        )}

        {resultsByCategories.map((results: Result[]) => (
          <CategoryContainer>
            {results.map((result: Result, index: number) => (
              <ResultDispatch
                result={result}
                key={index}
                model={project?.model}
              />
            ))}
          </CategoryContainer>
        ))}
      </ResultsContainer>
    </PageContainer>
  );
};

export const ResultDispatch = ({
  result,
  dashboard,
  model,
}: {
  result: Result;
  dashboard?: boolean;
  model?: Model;
}) => {
  const { t, i18n } = useTranslation(['common', 'project', 'model']);
  switch (result.type) {
    case 'nav':
      return <NavComponent result={result} model={model} />;
    case 'title':
      return <SectionTitle result={result} model={model} />;
    case 'subtitle':
      return <SectionSubtitle result={result} model={model} />;
    case 'text':
      return <TextBlock result={result} model={model} />;
    case 'globalScore':
      const text = i18n.language === 'fr' ? result.text_fr : result.text_en;
      return (
        <GlobalScoreComponent
          title={result.title!}
          subtitle={result.subtitle}
          uncertainty={result.uncertainty}
          score={result.score}
          code={result.code}
          dashboard={dashboard}
          model={model}
        >
          <p className="hxr">{text || result.text}</p>
        </GlobalScoreComponent>
      );
    case 'scoreCard':
      return (
        <GlobalScoreComponent
          title={result.title!}
          score={result.score}
          model={model}
        >
          {result.display! ? (
            <Abacus result={result} visible={result.display!} />
          ) : (
            <p>{t('project:results-page.result-empty')}</p>
          )}
        </GlobalScoreComponent>
      );
    case 'treemap':
      return (
        <ResultGraph
          result={result}
          model={model}
          fullWidth
          graph={<TreemapComponent result={result} />}
        />
      );
    case 'pie1D':
      if (dashboard) {
        return <Pie1DComponent result={result} model={model} />;
      }
      return (
        <ResultGraph
          result={result}
          model={model}
          graph={<Pie1DComponent result={result} model={model} />}
          legend={<Pie1DLegend result={result} model={model} />}
        />
      );
    case 'bar1D':
      return (
        <ResultGraph
          fullWidth
          result={result}
          model={model}
          graph={<Bar1DComponent result={result} model={model} />}
          legend={<Bar1DLegend result={result} model={model} />}
        />
      );
    case 'barSingle1D': {
      const fullWidth = (result?.data?.data.length || 0) > 3;
      return (
        <ResultGraph
          result={result}
          model={model}
          fullWidth={fullWidth}
          graph={<BarSingle1DComponent result={result} model={model} />}
          legend={<BarSingle1DLegend result={result} model={model} />}
        />
      );
    }
    case 'barStacked1D': {
      const fullWidth = (result?.data?.data?.length || 0) > 3;
      return (
        <ResultGraph
          result={result}
          model={model}
          fullWidth={fullWidth}
          graph={<BarStacked1DComponent result={result} />}
        />
      );
    }
    case 'barStacked2D':
      return (
        <ResultGraph
          result={result}
          model={model}
          graph={<BarStacked2DComponent result={result} />}
        />
      );
    case 'table':
      return <Table result={result} model={model} />;
  }
  return null;
};

export default Results;
